import React from "react";
import { Link } from "react-router-dom";
import { useSharedHomepageData } from "../../hooks/queries/useSharedHomepageData";
import { homepageApiService } from "../../services/homepageApiService";
import { LoadingSkeleton } from "../ui/LoadingStates";
import { ErrorMessage } from "../ui/ErrorStates";
import { debugLog } from "../../utils/debugLog";
import EventPostCard from "../common/EventPostCard";

interface UpcomingEventsWidgetContentProps {
  count?: number;
  showFeaturedOnly?: boolean;
}

/**
 * UpcomingEventsWidgetContent - Content-only version without container styling
 * Used by HomePage variations to avoid styling conflicts
 */
const UpcomingEventsWidgetContent: React.FC<
  UpcomingEventsWidgetContentProps
> = ({ count = 3, showFeaturedOnly = false }) => {
  const {
    data: homepageData,
    isLoading,
    error,
    refetch,
  } = useSharedHomepageData();

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
            <LoadingSkeleton height="h-4" width="w-32" />
          </div>
          <LoadingSkeleton height="h-4" width="w-20" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: count }).map((_, index) => (
            <div
              key={index}
              className="bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700"
            >
              <LoadingSkeleton height="h-48" width="w-full" />
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <LoadingSkeleton height="h-5" width="w-20" />
                  <LoadingSkeleton height="h-5" width="w-16" />
                </div>
                <LoadingSkeleton height="h-5" width="w-full" />
                <LoadingSkeleton height="h-4" width="w-3/4" />
                <LoadingSkeleton height="h-4" width="w-1/2" />
                <LoadingSkeleton height="h-4" width="w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        type="network"
        title="Failed to Load Events"
        message="Unable to load upcoming events. Please try again."
        onRetry={() => refetch()}
        showRetry={true}
        className="text-white"
      />
    );
  }

  const events = homepageData?.events || [];

  // Filter events based on widget parameters
  let filteredEvents = events;
  if (showFeaturedOnly) {
    filteredEvents = events.filter((event) => event.featured);
  }
  // Limit to requested count
  filteredEvents = filteredEvents.slice(0, count);

  // 🔍 DEBUG: Log the actual data being received
  debugLog("🎪 UpcomingEventsWidget Debug:", {
    isLoading,
    error: error?.message,
    homepageData: homepageData,
    totalEvents: events.length,
    filteredEventsCount: filteredEvents.length,
    filteredEvents: filteredEvents,
  });

  if (filteredEvents.length === 0) {
    return null; // Hide widget completely when no events
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            Upcoming Events
          </h2>
        </div>
        <Link
          to="/events"
          className="text-red-500 hover:text-red-400 transition-colors text-sm font-medium"
        >
          View All →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map((event) => {
          const imageUrl = event.featured_image
            ? homepageApiService.getImageUrl(event.featured_image, "event")
            : null;
          return (
            <EventPostCard
              key={event.id}
              event={event}
              imageUrl={imageUrl}
              from="home"
            />
          );
        })}
      </div>
    </>
  );
};

export default UpcomingEventsWidgetContent;
